import { useEffect, useRef } from 'react'
import classes from './ChatWidget.module.scss'
import FabGlow from './FabGlow'
import { FAB_SIZE_PX } from './fabPosition'
import {
  FLIGHT_DURATION_MS,
  FLIGHT_FADE_MS,
  FLIGHT_START_SIZE_PX,
  fadeKeyframes,
  flightKeyframes,
  FlightPoint,
} from './giniFlightPath'
import { IconBot } from './icons'

interface Props {
  /** FAB'in GÜNCEL kutusu (sürüklenmiş olabilir). `null` → uçuş atlanır. */
  getTarget: () => DOMRect | null
  /** Uçuş bitti (ya da yapılamadı): durum `idle`'a çekilir, FAB "pop" ile görünür. */
  onDone: () => void
}

const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

/**
 * Gini Tanıtım videosu kapanınca B-Gini logosunun ekran ortasından FAB'in yerine uçuşu (spec §12.4).
 * Yalnız süs: ekran okuyucudan gizli, tıklamayı engellemez. Animasyon Web Animations API ile
 * oynatılır (CSS `prefers-reduced-motion` bloğu WAAPI'yi durdurmaz; tercih burada ayrıca okunur).
 * WAAPI yoksa ya da hedef ölçülemiyorsa beklemeden `onDone` çağrılır.
 */
export default function GiniIntroFlightOverlay({ getTarget, onDone }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  useEffect(() => {
    const el = ref.current
    const rect = getTarget()
    if (!el || !rect || typeof el.animate !== 'function') {
      onDoneRef.current()
      return
    }

    const to: FlightPoint = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      size: rect.width || FAB_SIZE_PX,
    }
    const animation = prefersReducedMotion()
      ? el.animate(fadeKeyframes(to), { duration: FLIGHT_FADE_MS, easing: 'ease-out', fill: 'both' })
      : el.animate(
          flightKeyframes({ x: window.innerWidth / 2, y: window.innerHeight / 2, size: FLIGHT_START_SIZE_PX }, to),
          // Uçuşun başı yumuşak kalkar, sonu FAB'e yavaşlayarak oturur.
          { duration: FLIGHT_DURATION_MS, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'both' }
        )

    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      onDoneRef.current()
    }
    animation.onfinish = settle
    animation.oncancel = settle

    return () => {
      // Söküm (ör. StrictMode çift efekti) durumu değiştirmez; yalnız animasyon durdurulur.
      animation.onfinish = null
      animation.oncancel = null
      animation.cancel()
    }
    // Uçuş bir kez kurulur; hedef başlangıçta ölçülür.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div ref={ref} className={classes.giniFlight} aria-hidden='true'>
      <FabGlow />
      <span className={classes.fabIcon}>
        <IconBot />
      </span>
    </div>
  )
}
